import React, { useCallback, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AgentProvider } from '@/context/AgentContext';

import { useEventHandler } from '@/hooks/use-event-handler';

import {
  useAgentDetailQuery,
  useUpdateAgentMutation,
  useUpdateAgentStatusMutation,
} from '@/query/agent.query';

import type { AgentUpdateFormValues } from '@/schemas/agent.schema';

import { AgentTab } from './components/agent/agent-tab';
import { AgentDetailFooter } from './components/agent/footer';
import { AgentHeader } from './components/agent/header';
import { CallSettingsTab } from './components/call-setting/call-settings-tab';
import { AgentKnowledgeBaseTab } from './components/knowledge-base/agent-knowledge-base-tab';
import { PostCall } from './components/post-call/post-call';
import { TestAgentWeb } from './components/test-call';
import { TestAgentPhone } from './components/test-call-phone';
import { AgentToolsTab } from './components/tools/agent-tools-tab';
import { WebhookSettings } from './components/webhook/webhook';

import { AgentUpdateFormProvider } from '@/formik/agent.formik';

export default function AgentDetails(): React.ReactElement {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const [activeTab, setActiveTab] = useState<string>('agent');

  const { data: agentDetail, isLoading } = useAgentDetailQuery(agentId || '', {
    enabled: !!agentId,
  });

  const handleBack = () => {
    navigate('/agents');
  };

  const updateAgentStatusMutation = useUpdateAgentStatusMutation();

  const updateStatus = () => {
    if (!agentDetail?.id) return;
    updateAgentStatusMutation.mutate({ agentId: agentDetail.id, status: 'Published' });
  };

  useEventHandler({
    name: 'agents:publish',
    eventHandler: updateStatus,
  });

  const mutation = useUpdateAgentMutation();

  const handleSubmit = useCallback(
    (values: AgentUpdateFormValues) => {
      if (!agentDetail?.id) return;
      mutation.mutate({ agentId: agentDetail.id, data: values });
    },
    [agentDetail?.id, mutation]
  );

  const tabs = useMemo(() => {
    return [
      {
        value: 'agent',
        label: 'Agent',
        component: <AgentTab />,
      },
      {
        value: 'call-settings',
        label: 'Call settings',
        component: <CallSettingsTab />,
      },
      {
        value: 'analysis',
        label: 'Post-Call',
        component: <PostCall />,
      },
      {
        value: 'knowledge-base',
        label: 'Knowledge Base',
        component: agentDetail ? <AgentKnowledgeBaseTab agentId={agentDetail.id} /> : null,
      },
      {
        value: 'tools',
        label: 'Tools',
        component: agentDetail ? <AgentToolsTab agentId={agentDetail.id} /> : null,
      },
      {
        value: 'webhook',
        label: 'Webhook',
        component: <WebhookSettings />,
      },
    ];
  }, [agentDetail]);

  if (isLoading || !agentDetail) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading agent details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={handleBack} className="mb-4 p-3 h-9">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <AgentProvider data={agentDetail}>
          <TestAgentWeb />
          <TestAgentPhone />
        </AgentProvider>

        <AgentUpdateFormProvider initialValues={agentDetail} onSubmit={handleSubmit}>
          {/* Agent Header */}
          <AgentHeader agentDetail={agentDetail} />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="text-foreground h-auto gap-2 rounded-none border-b bg-transparent px-0 py-1 w-full justify-start">
              {tabs.map(tab => {
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="hover:bg-primary/10 hover:text-primary data-[state=active]:after:bg-primary data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 after:-mb-1.5 after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {tabs.map(tab => (
              <TabsContent key={tab.value} value={tab.value} className="space-y-6 mt-6">
                {activeTab === tab.value ? tab.component : null}
              </TabsContent>
            ))}
          </Tabs>
          {/* Sticky Save/Cancel Buttons */}
          <AgentDetailFooter isPending={mutation.isPending} />
        </AgentUpdateFormProvider>
      </div>
    </div>
  );
}
